import { useEffect, useState } from 'react';
import { recordEvent, getLocalSummary, getLocalSongStats, getLocalArtworkStats } from '@/lib/localAnalytics';

interface DeviceInfo {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  operatingSystem: string;
  browser: string;
  screenWidth: number;
  screenHeight: number;
  language: string;
  timezone: string;
  userAgent: string;
}

interface LocationInfo {
  country?: string;
  hashedId?: string;
}

// Generate unique session ID (in-memory only, never persisted)
const generateSessionId = () => {
  return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Detect device information
const getDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;

  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
    deviceType = 'mobile';
  } else if (/Tablet|iPad/.test(userAgent)) {
    deviceType = 'tablet';
  }

  let operatingSystem = 'Unknown';
  if (userAgent.includes('Windows')) operatingSystem = 'Windows';
  else if (userAgent.includes('Mac')) operatingSystem = 'macOS';
  else if (userAgent.includes('Linux')) operatingSystem = 'Linux';
  else if (userAgent.includes('Android')) operatingSystem = 'Android';
  else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) operatingSystem = 'iOS';

  let browser = 'Unknown';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
  else if (userAgent.includes('Edg')) browser = 'Edge';
  else if (userAgent.includes('Opera')) browser = 'Opera';

  return {
    deviceType,
    operatingSystem,
    browser,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent
  };
};

// Country from timezone only — no IP, no network calls
const getLocationInfo = (): LocationInfo => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeZoneCountryMap: { [key: string]: string } = {
      'Europe/Stockholm': 'Sweden',
      'Europe/London': 'United Kingdom',
      'America/New_York': 'United States',
      'America/Los_Angeles': 'United States',
      'Europe/Berlin': 'Germany',
      'Europe/Paris': 'France',
      'Europe/Madrid': 'Spain',
      'Europe/Rome': 'Italy',
      'Europe/Amsterdam': 'Netherlands',
      'Asia/Tokyo': 'Japan',
      'Australia/Sydney': 'Australia',
    };
    return { country: timeZoneCountryMap[timeZone] || 'Other' };
  } catch {
    return {};
  }
};

export const useAnalytics = () => {
  const [sessionId] = useState(() => generateSessionId());
  const [deviceInfo] = useState(() => getDeviceInfo());
  const [locationInfo] = useState<LocationInfo>(() => getLocationInfo());
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize analytics — completely non-blocking, fully local
  useEffect(() => {
    const initAnalytics = () => {
      setIsInitialized(true);

      const startAnalytics = () => {
        setTimeout(() => {
          try {
            recordEvent({
              type: 'session',
              deviceType: deviceInfo.deviceType,
              operatingSystem: deviceInfo.operatingSystem,
              browser: deviceInfo.browser,
              country: locationInfo.country,
            });
          } catch (error) {
            console.error('Error storing user analytics:', error);
          }
        }, 2000);
      };

      if (document.readyState === 'complete') {
        startAnalytics();
      } else {
        window.addEventListener('load', startAnalytics, { once: true });
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initAnalytics(), { timeout: 5000 });
    } else {
      setTimeout(initAnalytics, 1000);
    }
  }, [deviceInfo, locationInfo]);

  const trackSongPlay = async (
    songTitle: string,
    songArtist: string,
    _songSrc: string,
    playDuration: number,
    completed: boolean = false
  ) => {
    if (!isInitialized) return;
    try {
      recordEvent({
        type: 'song_play',
        songTitle,
        songArtist,
        playDuration,
        completed,
        deviceType: deviceInfo.deviceType,
        country: locationInfo.country,
      });
    } catch (error) {
      console.error('Error tracking song play:', error);
    }
  };

  const trackArtworkView = async (artworkName: string, viewDuration: number = 0) => {
    if (!isInitialized) return;
    try {
      recordEvent({
        type: 'artwork_view',
        artworkName,
        viewDuration,
        deviceType: deviceInfo.deviceType,
        country: locationInfo.country,
      });
    } catch (error) {
      console.error('Error tracking artwork view:', error);
    }
  };

  const trackPageView = async (pagePath: string, _referrer?: string) => {
    if (!isInitialized) return;
    try {
      recordEvent({
        type: 'page_view',
        pagePath,
        deviceType: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        operatingSystem: deviceInfo.operatingSystem,
        country: locationInfo.country,
      });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  };

  // Local aggregated stats (this device only)
  const getAnalyticsSummary = async () => {
    try {
      return {
        summary: getLocalSummary(),
        songStats: getLocalSongStats(),
        artworkStats: getLocalArtworkStats(),
      };
    } catch (error) {
      console.error('Error getting analytics summary:', error);
      return null;
    }
  };

  return {
    sessionId,
    deviceInfo,
    locationInfo,
    isInitialized,
    trackSongPlay,
    trackArtworkView,
    trackPageView,
    getAnalyticsSummary
  };
};
